import { useState, useCallback, useEffect, useRef } from 'react';
import { getApiUrl } from '../config';

export interface InvitableUser {
  id: string;
  name: string;
  nickname?: string;
  avatar?: string;
  rating?: number;
  position?: string;
  gamesPlayedTogether?: number;
  matchesPlayed?: number;
  status: 'available' | 'invited' | 'in_match';
  source: 'group' | 'friend';
  groupName?: string;
}

export interface GroupPendingMember {
  id: string;
  guestName: string;
  guestPosition: string;
  guestRating: number;
}

export interface InvitableGroup {
  groupId: string;
  groupName: string;
  users: InvitableUser[];
  pendingMembers?: GroupPendingMember[];
}

export interface InvitableUsersData {
  groups: InvitableGroup[];
  friends: InvitableUser[];
}

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export function useInvitableUsers(
  matchId: string | undefined,
  enabled: boolean = false
) {
  const [data, setData] = useState<InvitableUsersData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  const fetchInvitableUsers = useCallback(async () => {
    if (!matchId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        getApiUrl(`/api/matches/${matchId}/invitable-users`),
        { headers: getAuthHeaders() }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || 'Error al cargar usuarios invitables');
        return;
      }
      const body = await res.json();
      setData({ groups: body.groups || [], friends: body.friends || [] });
    } catch {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    if (enabled && matchId && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchInvitableUsers();
    }
    if (!enabled) {
      fetchedRef.current = false;
    }
  }, [enabled, matchId, fetchInvitableUsers]);

  const refetch = useCallback(() => {
    fetchedRef.current = false;
    fetchInvitableUsers();
  }, [fetchInvitableUsers]);

  const sendInvitations = useCallback(
    async (userIds: string[]): Promise<boolean> => {
      if (!matchId || userIds.length === 0) return false;
      try {
        const res = await fetch(getApiUrl(`/api/matches/${matchId}/invite`), {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ userIds }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setError(body.error || 'Error al enviar invitaciones');
          return false;
        }
        return true;
      } catch {
        setError('Error de conexión');
        return false;
      }
    },
    [matchId]
  );

  const addGuest = useCallback(
    async (guestName: string, guestPosition?: string, guestRating?: number, groupPendingMemberId?: string): Promise<boolean> => {
      if (!matchId) return false;
      try {
        const res = await fetch(getApiUrl(`/api/matches/${matchId}/add-guest`), {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            guestName: guestName.trim(),
            ...(guestPosition && { guestPosition }),
            ...(guestRating && { guestRating }),
            ...(groupPendingMemberId && { groupPendingMemberId }),
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setError(body.error || 'Error al agregar invitado');
          return false;
        }
        return true;
      } catch {
        setError('Error de conexión');
        return false;
      }
    },
    [matchId]
  );

  const proposeGuest = useCallback(
    async (guestName: string, guestPosition?: string, guestRating?: number): Promise<boolean> => {
      if (!matchId) return false;
      try {
        const res = await fetch(getApiUrl(`/api/matches/${matchId}/guest-requests`), {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            guestName: guestName.trim(),
            ...(guestPosition && { guestPosition }),
            ...(guestRating && { guestRating }),
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setError(body.error || 'Error al proponer invitado');
          return false;
        }
        return true;
      } catch {
        setError('Error de conexión');
        return false;
      }
    },
    [matchId]
  );

  return { data, loading, error, refetch, sendInvitations, addGuest, proposeGuest };
}
